import { useState, useEffect, useCallback, useRef } from 'react';
import SessionsApi from '../api/sessions';
import { Session } from '../api/types/session';
import { useWebSocket } from '../contexts/useWebSocket';
import { traceLog } from '../utils/requestTrace';

const DEFAULT_POLL_MS = 45000;

export const useCurrentSession = () => {
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssociationModal, setShowAssociationModal] = useState(false);
  const { isConnected, onSessionStatusChange, offSessionStatusChange } = useWebSocket();
  const inFlightRequestRef = useRef<Promise<void> | null>(null);
  const hasBootstrappedRef = useRef(false);
  const pollMsRef = useRef(
    Number(import.meta.env.VITE_CURRENT_SESSION_POLL_MS ?? DEFAULT_POLL_MS) || DEFAULT_POLL_MS
  );

  const fetchCurrentSession = useCallback(async (source = 'manual') => {
    if (inFlightRequestRef.current) {
      traceLog('current-session', `skip fetch (${source}) - request already in flight`);
      return inFlightRequestRef.current;
    }

    traceLog('current-session', `fetch start (${source})`);
    const request = (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await SessionsApi.fetchCurrentSession(`useCurrentSession:${source}`);
        if (!response.data) {
          // No active session
          setCurrentSession(null);
          traceLog('current-session', `fetch complete (${source}) - no active session`);
          return;
        }
        setCurrentSession(response.data);
        traceLog('current-session', `fetch complete (${source}) - active session`, {
          sessionId: response.data.id
        });
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number } };
          if (axiosError.response?.status === 404) {
            // No active session - this is not an error
            setCurrentSession(null);
            setError(null);
          } else {
            setError('Failed to fetch current session');
          }
        } else {
          setError('Failed to fetch current session');
        }
      } finally {
        setLoading(false);
        inFlightRequestRef.current = null;
      }
    })();

    inFlightRequestRef.current = request;
    return request;
  }, []);

  const stopSession = useCallback(async () => {
    if (!currentSession) return;

    try {
      // Update the session to set endDate to now
      await SessionsApi.updateSession(currentSession.id, {
        endDate: new Date()
      });
      // Refresh current session after stopping
      await fetchCurrentSession('stop-session');

      // force reload, remove when we come up with a better solution
      window.location.reload();
    } catch (err: unknown) {
      console.error('Failed to stop session:', err);
      setError('Failed to stop session');
    }
  }, [currentSession, fetchCurrentSession]);

  const handleAssociationComplete = useCallback(() => {
    setShowAssociationModal(false);
    // Clear current session and force reload to show updated state
    setCurrentSession(null);
    window.location.reload();
  }, []);

  // Handle real-time session status changes via WebSocket.
  useEffect(() => {
    if (!isConnected) return;

    const handleSessionStatusChange = (data: Record<string, unknown>) => {
      if (data.status === 'started') {
        // Session started - fetch the new session data
        fetchCurrentSession('websocket-started');
      } else if (data.status === 'ended') {
        // Check if PlayerCharacter association is needed
        if (data.playerAssociationNeeded) {
          // Keep the session data but show association modal
          setShowAssociationModal(true);
        } else {
          // Session ended normally - clear current session
          setCurrentSession(null);
        }
      }
    };

    // Listen for session status changes
    onSessionStatusChange(handleSessionStatusChange);

    return () => {
      offSessionStatusChange(handleSessionStatusChange);
    };
  }, [isConnected, onSessionStatusChange, offSessionStatusChange, fetchCurrentSession]);

  // Bootstraps one initial fetch per hook owner mount.
  useEffect(() => {
    if (hasBootstrappedRef.current) {
      return;
    }
    hasBootstrappedRef.current = true;
    fetchCurrentSession('initial-mount');
  }, [fetchCurrentSession]);

  // Hybrid model: websocket updates + low-frequency fallback poll.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCurrentSession('poll');
    }, pollMsRef.current);

    return () => {
      clearInterval(interval);
    };
  }, [fetchCurrentSession]);

  return {
    currentSession,
    loading,
    error,
    refetch: fetchCurrentSession,
    stopSession,
    showAssociationModal,
    setShowAssociationModal,
    handleAssociationComplete
  };
};
